import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { Provider } from "react-redux";
import { AccountMenu } from "tsi-common-react/src/apps/authentication/components/AccountMenu/index";
import { BasketLineVariant } from "tsi-common-react/src/apps/checkout/constants";
import { BasketMenuIcon } from "tsi-common-react/src/apps/checkout/containers/BasketMenuIcon";
import { RichNavigation } from "tsi-common-react/src/apps/rich-navigation/RichNavigation";
import { ValueProps } from "tsi-common-react/src/apps/rich-navigation/RichNavigationLinkCollection";
import { Link } from "tsi-common-react/src/common/Link";
import { Messages } from "tsi-common-react/src/common/Message";
import { RichText } from "tsi-common-react/src/common/RichText";
import { SVGIcon } from "tsi-common-react/src/common/SVGIcon";
import { isoWebPageURL } from "tsi-common-react/src/models/nominals";
import { useCurrentBreakpoint } from "tsi-common-react/src/utils/hooks";
import {
    isPageInsideIFrame as isPageInsideIFrameCheck,
    usePageSetting,
} from "tsi-common-react/src/utils/settings";
import { useIsSSR } from "tsi-common-react/src/utils/ssr";
import { usePageURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import { reverse, templates } from "@reactivated";

import { constants } from "@reactivated/constants";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { FinancingModalTriggerMinicart } from "../src/Checkout/components/FinancingModalTriggerMinicart";
import { SiteHeaderLanguageToggle } from "../src/Common/SiteHeaderLanguageToggle";
import { MainMenu } from "../src/Common/components/MainMenu";
import { SearchNav } from "../src/Common/components/SearchNav";
import { IMenuItem } from "../src/Common/menu.interface";
import { store } from "../src/store";

import styles from "./SiteHeaderTemplate.module.scss";

const AppSlug = constants["tsicommon.types.AppSlug"];
type AppSlug = (typeof AppSlug)[keyof typeof AppSlug];

type LogoVariant = {
    title: string;
    linkClassName: string;
    ariaLabel: string;
    logoClassName?: string;
    logoSvgClassName?: string;
    logoName: string;
    logoTitle: string;
    role?: string;
    target?: string;
};

type LogoVariants = {
    [key in AppSlug]?: LogoVariant;
};

export const logoVariants = {
    tempurpedic: {
        title: t`TempurPedic`,
        linkClassName: "site-nav__site-logo-link",
        ariaLabel: t`Tempur-Pedic`,
        logoClassName: "site-nav__logo",
        logoName: "tempurpedicLogo",
        logoTitle: t`Tempur-Pedic`,
    },
    tempurca: {
        title: t`TempurPedic`,
        linkClassName: "site-nav__site-logo-link",
        ariaLabel: t`Tempur-Pedic`,
        logoClassName: "site-nav__logo site-nav__logo--canada",
        logoName: "tempurpedicLogo",
        logoTitle: t`Tempur-Pedic`,
    },
    sealywarranty: {
        title: t`Sealy`,
        linkClassName: "site-nav__site-logo-link",
        ariaLabel: t`Sealy`,
        logoClassName: "sealy-logo",
        logoSvgClassName: "sealy-logo",
        logoName: "sealyLogo",
        logoTitle: t`Sealy Logo`,
    },
    sealyemployees: {
        title: t`Sealy | Stearns & Foster`,
        linkClassName: "site-nav__site-logo-link",
        ariaLabel: t`Sealy | Stearns & Foster`,
        logoSvgClassName: "logo-alt",
        logoName: "sealyStearnsLogo",
        logoTitle: t`Sealy Logo`,
    },
    sealyretail: {
        title: t`Sealy | Stearns & Foster`,
        linkClassName: "site-nav__site-logo-link",
        ariaLabel: t`Sealy | Stearns & Foster`,
        logoSvgClassName: "logo-alt",
        logoName: "sealyStearnsLogo",
        logoTitle: t`Sealy | Stearns & Foster`,
    },
    stearnswarranty: {
        title: t`Stearns & Foster`,
        linkClassName: "site-nav__site-logo-link",
        ariaLabel: t`Stearns & Foster`,
        logoSvgClassName: "site-nav__logo stearns-logo",
        logoName: "stearnsWarrantyLogo",
        logoTitle: t`Stearns & Foster`,
    },
} as const satisfies LogoVariants;

const getLogoVariant = (appSlug: string | null): LogoVariant | null => {
    // defaults can be removed and added to variant options if needed
    const defaults = {
        role: "listitem",
        target: "_top",
    };
    // If any sub-sites which will use tempurpedic logo or its own logo,
    // should be added here otherwise it will return null
    switch (appSlug) {
        case AppSlug.TEMPURWARRANTY:
        case AppSlug.EMPLOYEES:
        case AppSlug.FRIENDS:
        case AppSlug.ASSOCIATES:
        case AppSlug.SALE2:
        case AppSlug.TEMPURPEDIC:
            return { ...defaults, ...logoVariants.tempurpedic };
        case AppSlug.TEMPURCA:
            return { ...defaults, ...logoVariants.tempurca };
        case AppSlug.SEALYWARRANTY:
            return { ...defaults, ...logoVariants.sealywarranty };
        case AppSlug.SEALYEMPLOYEES:
            return { ...defaults, ...logoVariants.sealyemployees };
        case AppSlug.SEALYRETAIL:
            return { ...defaults, ...logoVariants.sealyretail };
        case AppSlug.STEARNSWARRANTY:
            return { ...defaults, ...logoVariants.stearnswarranty };
        default:
            // Prevent out of context logo rendering and flashing effect
            return null;
    }
};

export const Template = (props: templates.SiteHeaderTemplate) => {
    const isSSR = useIsSSR();
    const viewport = useCurrentBreakpoint();
    const [mobilePanelVisible, setMobilePanelVisible] = useState(false);
    const [dropdownOffsetTop, setDropdownOffsetTop] = useState(0);
    const siteHeaderRef = useRef<HTMLElement | null>(null);
    const logoRef = useRef<HTMLDivElement | null>(null);
    const { promo_settings, messages, rich_navigation, top_nav_menu } = props;

    const homeURL = usePageURL("home");
    const enableBasketPrequalSDK = usePageSetting("show_prequal_cart") || false;
    const basketURL = isoWebPageURL.wrap(reverse("basket:summary"));
    const checkoutURL = isoWebPageURL.wrap(reverse("checkout:index"));
    const isPageInsideIFrame = isSSR ? false : isPageInsideIFrameCheck();
    const appSlug = usePageSetting("app_slug");
    const enableEcom = usePageSetting("enable_ecom");

    // Map top_nav_menu data to IMenuItem[]
    const menuItems: IMenuItem[] = Array.isArray(top_nav_menu.menu.value)
        ? (top_nav_menu.menu.value as IMenuItem[])
        : [];

    // Render logo for tempurpedic and sub-sites
    const logoVariant = getLogoVariant(appSlug);

    const toggleMobilePanel = () => {
        if (viewport.belowMobile) {
            setMobilePanelVisible((o) => !o);
        }
    };

    useEffect(() => {
        const window = getWindow();
        const document = getDocument();
        if (!window || !document) {
            return;
        }
        const resizeHandler = () => {
            if (window.innerWidth >= 768) {
                // Close mobile panel if viewport is resized above mobile breakpoint
                // Remove modal-open class to remove styling effects
                document.body.classList.remove("modal-open");
                setMobilePanelVisible(false);
            }
        };
        // Initialize rich navigation resize handler
        resizeHandler();
        window.addEventListener("resize", resizeHandler);
        return () => {
            // Cleanup rich navigation resize handler
            window.removeEventListener("resize", resizeHandler);
        };
    }, []);

    useEffect(() => {
        if (!logoRef.current) return;

        const window = getWindow();
        const document = getDocument();
        if (!window || !document) {
            return;
        }

        const headerElem = siteHeaderRef.current;
        const logoElem = logoRef.current?.querySelector("svg");

        if (headerElem && logoElem) {
            // Get scroll position
            const getScrollTop =
                window.pageYOffset || document.documentElement.scrollTop;
            // Calculate dropdown offset
            const headerBottom = headerElem.getBoundingClientRect().bottom;
            setDropdownOffsetTop(headerBottom + getScrollTop);
        } else {
            console.warn(
                "Header or Logo element not found during offset calculation.",
            );
        }
    }, [siteHeaderRef, logoRef]);

    return (
        <>
            {promo_settings.show_promo_header && (
                <nav className="search-nav" role="search">
                    <div className="l-max-width">
                        <a
                            className="screen-reader-skip"
                            href="#main-content"
                        >{t`Skip to main content`}</a>
                        {promo_settings.promo_header?.value.map((promo, i) => (
                            <React.Fragment key={`key:${i}`}>
                                {promo.type === "promo" && (
                                    <>
                                        <div className="search-nav__promo">
                                            {promo.value.copy && (
                                                <RichText
                                                    html={promo.value.copy}
                                                />
                                            )}
                                        </div>
                                        <div className="search-nav__promo--mobile">
                                            {promo.value.mobile_copy ? (
                                                <RichText
                                                    html={
                                                        promo.value.mobile_copy
                                                    }
                                                />
                                            ) : (
                                                <>
                                                    {promo.value.copy && (
                                                        <RichText
                                                            html={
                                                                promo.value.copy
                                                            }
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}
                            </React.Fragment>
                        ))}
                        <div className="search-nav__links">
                            {appSlug === AppSlug.TEMPURCA && (
                                <SiteHeaderLanguageToggle />
                            )}
                            <Provider store={store}>
                                <SearchNav />
                            </Provider>
                        </div>
                    </div>
                </nav>
            )}

            {messages && <Messages messages={messages} />}

            <header className="site-header" ref={siteHeaderRef}>
                <nav className="site-nav l-max-width">
                    <div>
                        {rich_navigation && (
                            <button
                                className="mobile-toggle"
                                onClick={toggleMobilePanel}
                            >
                                {mobilePanelVisible ? (
                                    <SVGIcon name="xIcon" alt={t`Close icon`} />
                                ) : (
                                    <SVGIcon
                                        name="barsIcon"
                                        alt={t`Hamburger navigation icon`}
                                    />
                                )}
                            </button>
                        )}
                        <h1>
                            {logoVariant && (
                                <Link
                                    title={logoVariant.title}
                                    className={logoVariant.linkClassName}
                                    href={homeURL}
                                    aria-label={logoVariant.ariaLabel}
                                    role={logoVariant.role}
                                    target={logoVariant.target}
                                >
                                    <div ref={logoRef}>
                                        <SVGIcon
                                            name={logoVariant.logoName}
                                            className={
                                                logoVariant.logoClassName
                                            }
                                            svgClassName={
                                                logoVariant.logoSvgClassName
                                            }
                                            alt={logoVariant.logoTitle}
                                        />
                                    </div>
                                </Link>
                            )}
                        </h1>
                        <nav
                            className={classNames({
                                [styles.mainMenuContainer]: true,
                                "rich-navigation--active": !!rich_navigation,
                            })}
                        >
                            <MainMenu
                                menuItems={menuItems}
                                dropdownOffsetTop={dropdownOffsetTop}
                            />
                        </nav>
                    </div>

                    {enableEcom && (
                        <ul>
                            <li>
                                <Provider store={store}>
                                    <AccountMenu />
                                </Provider>
                            </li>
                            <li>
                                <Provider store={store}>
                                    <BasketMenuIcon
                                        basketURL={basketURL}
                                        checkoutURL={checkoutURL}
                                        isPageInsideIFrame={isPageInsideIFrame}
                                        basketLineVariant={
                                            BasketLineVariant.MINIMAL_ENHANCED
                                        }
                                        buildPromoComponents={(closeModal) => {
                                            if (!enableBasketPrequalSDK) {
                                                return null;
                                            }
                                            return (
                                                <Provider store={store}>
                                                    <FinancingModalTriggerMinicart
                                                        closeModal={closeModal}
                                                    />
                                                </Provider>
                                            );
                                        }}
                                        isSSR={isSSR}
                                    />
                                </Provider>
                            </li>
                        </ul>
                    )}
                </nav>

                {mobilePanelVisible &&
                    rich_navigation &&
                    viewport.belowMobile && (
                        <nav
                            id="mobile-panel"
                            className="rich-navigation"
                            aria-hidden={!mobilePanelVisible}
                        >
                            <RichNavigation
                                openMobileContentDefaultState={true}
                                {...rich_navigation}
                            />
                            <ValueProps
                                value_props={rich_navigation.value_props}
                                enableValuePropsOnMobile
                            />
                        </nav>
                    )}
            </header>
        </>
    );
};
